import MainTitle from "../../components/ui/MainTitle/MainTitle";
import { useTranslation } from "react-i18next";
import { Table, TableProps } from "antd";
import React, { useMemo, useState } from "react";
import { IContractForTable } from "../../models/Contract";
import dayjs from "dayjs";
import { ColumnsType } from "antd/es/table";
import { useGetContractsQuery } from "../../api/queries";
import dots from "../../assets/images/icons/dots.svg";
import viewIcon from "../../assets/images/icons/eye-dark.svg";
import downloadIcon from "../../assets/images/icons/download.svg";
import assignIcon from "../../assets/images/icons/assign.svg";
import html2pdf from "html2pdf.js";
import AssignContractPopup from "../../components/AssignContractPopup/AssignContractPopup";
import CreateContract from "./CreateContract/CreateContract";
import plusIconWhite from "../../assets/images/icons/plus-white.svg";
import CustomButton from "../../components/ui/CustomButton/CustomButton";

const languageSelectItems = [
  { id: 0, label: "English" },
  { id: 1, label: "Greek" },
];

const Contracts = () => {
  const { t } = useTranslation();

  const [tableParams, setTableParams] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 10,
  });
  const [selectedContract, setSelectedContract] = useState<null | IContractForTable>(null);
  const [showCreateContractPopup, setShowCreateContractPopup] = useState<boolean>(false);
  const [selectedContractForAssign, setSelectedContractForAssign] =
    useState<null | IContractForTable>(null);

  const {
    data = { data: [], totalCount: 0 },
    isLoading,
    refetch,
  } = useGetContractsQuery({
    currentPage: tableParams.current,
    pageSize: tableParams.pageSize,
  });

  const totalCount = useMemo(() => {
    if (!data) return 0;
    return data.totalCount;
  }, [data]);

  const handleDownloadPDF = (contract: IContractForTable) => {
    const element = document.createElement("div");
    element.innerHTML = contract.content;

    const opt = {
      margin: 0.5,
      filename: `${contract.title}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    };

    html2pdf().set(opt).from(element).save();
  };

  const handleTableChange: TableProps<IContractForTable>["onChange"] = (pagination) => {
    setTableParams({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  const columns: ColumnsType<IContractForTable> = [
    {
      title: t("ContractTile"),
      render: (data: IContractForTable) => {
        return data.title;
      },
    },
    {
      title: t("Version"),
      render: (data: IContractForTable) => {
        return `v${data.version}`;
      },
    },
    {
      title: t("Language"),
      render: (data: IContractForTable) => {
        const lang =
          languageSelectItems.find((el) => Number(el.id) === Number(data.language)) || null;
        return lang ? lang.label : "-";
      },
    },
    {
      title: t("CreatedOn"),
      render: (data: IContractForTable) => {
        return `${dayjs(data.createdAt).format("DD/MM/YYYY")}`;
      },
    },
    {
      title: t("Action"),
      width: "150px",
      render: (data: IContractForTable) => {
        return (
          <div className={"action-wrapper"}>
            <img src={dots} alt={"action"} />
            <ul className={"dropdown"}>
              <li
                className={"dropdown-item"}
                onClick={() => {
                  setSelectedContract(data);
                  setShowCreateContractPopup(true);
                }}
              >
                <img src={viewIcon} alt={"View"} />
                {t("View")}
              </li>
              <li className={"dropdown-item"} onClick={() => handleDownloadPDF(data)}>
                <img src={downloadIcon} alt={"Download"} />
                {t("Download")}
              </li>
              <li className={"dropdown-item"} onClick={() => setSelectedContractForAssign(data)}>
                <img src={assignIcon} alt={"Assign"} />
                {t("Assign")}
              </li>
            </ul>
          </div>
        );
      },
    },
  ];

  return (
    <div className={"page-content gyms-page"}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <MainTitle text={t("Contracts")} />
        <CustomButton
          text={t("AddNew")}
          type={"button"}
          typeStyle={"fill"}
          icon={plusIconWhite}
          altIcon={"add"}
          functionOnClick={() => setShowCreateContractPopup(true)}
          style={{ marginBottom: "16px", width: "180px" }}
        />
      </div>

      <Table
        className={"table"}
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={data.data}
        pagination={{ ...tableParams, total: totalCount }}
        loading={isLoading}
        onChange={handleTableChange}
      />

      {selectedContractForAssign && (
        <AssignContractPopup
          contractId={selectedContractForAssign.contractId}
          contractVersionId={selectedContractForAssign.contractVersionId}
          closeFn={() => setSelectedContractForAssign(null)}
        />
      )}

      {showCreateContractPopup && (
        <CreateContract
          selectedContract={selectedContract}
          setSelectedContract={setSelectedContract}
          refetch={refetch}
          languageSelectItems={languageSelectItems}
          closePopup={() => setShowCreateContractPopup(false)}
        />
      )}
    </div>
  );
};

export default Contracts;
